import { z } from 'zod';
import { pruefeZeitplan } from './workflows-zeitplan.js';
export * from './workflows-zeitplan.js';
/**
 * **Der Vertrag der Workflows** — Spec `docs/superpowers/specs/2026-11-04-workflows-design.md`
 * (`D-2`, `D-3`, `D-4`, `D-6` bis `D-9`, Abschnitt 3), Task 1 des Plans
 * `docs/superpowers/plans/2026-11-04-workflows-umsetzung.md`.
 *
 * **Ein Katalog als Daten, den Oberfläche und Server gleich lesen.** Welche Knoten
 * es gibt, welche Ausgänge sie haben, ob sie nach aussen wirken und wie ihre
 * Einstellungen aussehen, steht GENAU EINMAL hier: in {@link KNOTENKATALOG} und
 * {@link EINSTELLUNGEN_SCHEMAS}. Die Oberfläche baut daraus die Knotenbibliothek und
 * die Eigenschaftsleiste, der Server prüft mit denselben Schemas, der Läufer liest
 * `ausgaenge` und `aussenwirkung`. Ein neuer Knotentyp ist eine Zeile hier und ein
 * Ausführer im Läufer — kein zweites Verzeichnis.
 *
 * **Die Graphprüfung steht einmal** ({@link pruefeGraph}): dieselbe Funktion zeigt
 * im Builder die Befunde und weist auf dem Server das Scharfschalten ab. Die
 * Datenbank prüft nur die Form des `jsonb` (Konvention `sql:jsonb`), die Bedeutung
 * prüft Zod.
 *
 * **Zwei Formen des Graphen.** {@link graphFormSchema} prüft nur die Gestalt
 * (Knoten mit bekanntem Typ, Kanten, Einstellungen als Objekt) — damit speichert
 * der Server einen unfertigen Entwurf. {@link graphSchema} ist dieselbe Gestalt mit
 * nach `typ` unterschiedenen Einstellungen (für typisierte Leser), und
 * {@link gueltigerGraphSchema} verlangt zusätzlich, dass {@link pruefeGraph} nichts
 * findet — die Form eines scharfgeschalteten Graphen.
 *
 * **Beträge und Kennungen sind Zeichenketten** (Hausregel 1), Platzhalterfelder
 * ebenfalls: Was `{{ausloeser.betrag}}` trägt, ist erst im Lauf bekannt.
 */
// ---------------------------------------------------------------------------
// Grenzen
// ---------------------------------------------------------------------------
/** Höchstens so viele Knoten je Fassung (`D-4`). */
export const WORKFLOW_HOECHST_KNOTEN = 50;
/** Höchstens so viele Schritte je Lauf, ein Schritt je besuchtem Knoten (`D-4`). */
export const WORKFLOW_HOECHST_SCHRITTE = 100;
/** Längste Verzögerung in Minuten: 30 Tage (`D-14`). */
export const WORKFLOW_HOECHST_VERZOEGERUNG_MINUTEN = 30 * 24 * 60;
// ---------------------------------------------------------------------------
// Knotentypen, Arten, Ausgänge
// ---------------------------------------------------------------------------
/** Alle Knotentypen: vier Auslöser (`D-3`) und die zehn aus Spec 3.2 (`D-9`). */
export const KNOTENTYPEN = [
    'ausloeser_ereignis',
    'ausloeser_zeitplan',
    'ausloeser_webhook_eingang',
    'ausloeser_manuell',
    'bedingung',
    'verzoegerung',
    'variable_setzen',
    'protokoll',
    'stopp',
    'http_anfrage',
    'webhook_senden',
    'email_senden',
    'kontierung_anlegen',
];
export const knotentypSchema = z.enum(KNOTENTYPEN);
export const knotenArtSchema = z.enum(['ausloeser', 'bedingung', 'aktion', 'ablauf']);
/** Ein Ausgang eines Knotens: `weiter` für alle, `ja`/`nein` für die Bedingung. */
export const knotenAusgangSchema = z.enum(['weiter', 'ja', 'nein']);
// ---------------------------------------------------------------------------
// Kontext und Platzhalter (`D-7`)
// ---------------------------------------------------------------------------
/** Die Wurzeln des Laufkontexts, in die ein Platzhalter greifen darf. */
export const KONTEXT_WURZELN = ['ausloeser', 'schritte', 'variablen', 'workflow', 'lauf', 'jetzt'];
const KONTEXT_PFAD_MUSTER = /^(ausloeser|schritte|variablen|workflow|lauf|jetzt)(\.[A-Za-z0-9_-]+)*$/;
/** Ist `pfad` ein syntaktisch gültiger {@link KontextPfad}? (Ob er im Lauf existiert, sagt erst der Kontext.) */
export function istKontextPfad(pfad) {
    return KONTEXT_PFAD_MUSTER.test(pfad);
}
export const kontextPfadSchema = z
    .string()
    .trim()
    .refine(istKontextPfad, {
    message: 'Dieser Pfad zeigt in keinen Teil des Laufs; bitte einen Pfad aus der Platzhalterliste wählen.',
});
/** Trägt der Text mindestens einen Platzhalter `{{…}}`? */
export function enthaeltPlatzhalter(text) {
    return /\{\{[^{}]*\}\}/.test(text);
}
/**
 * **Geheimnisse** (Spec-Nachtrag 0.4 Nr. 5): `{{geheimnis.<name>}}` ist KEIN Pfad in den
 * Laufkontext — der Kontext trägt keine Geheimnisse. Der Läufer löst den Platzhalter erst
 * beim Senden auf, und nur in `http_anfrage.kopfzeilen[].wert` und `http_anfrage.rumpf`;
 * überall sonst meldet {@link pruefeGraph} `geheimnis_unzulaessig`. `ersetze()` lässt ihn
 * unangetastet stehen, {@link maskiereGeheimnisse} macht ihn für Protokolle zu `•••`.
 */
export const GEHEIMNIS_NAME_MUSTER = /^[a-z][a-z0-9_]{1,40}$/;
export const geheimnisNameSchema = z
    .string()
    .regex(GEHEIMNIS_NAME_MUSTER, 'Ein Geheimnisname beginnt mit einem Kleinbuchstaben und hat 2 bis 41 Zeichen aus a-z, 0-9 und _.');
/** Das Platzhaltermuster eines Geheimnisses; je Aufruf frisch, weil global. */
function geheimnisMuster() {
    return /\{\{\s*geheimnis\.([^{}\s]*)\s*\}\}/g;
}
/** Die Namen aller Geheimnis-Platzhalter in `text`, in der Reihenfolge ihres Vorkommens (auch ungültige Namen). */
export function geheimnisNamen(text) {
    return [...text.matchAll(geheimnisMuster())].map((m) => m[1] ?? '');
}
/** Ersetzt jeden Geheimnis-Platzhalter durch `•••` — für Schrittprotokoll, Prüfprotokoll und Anzeige. */
export function maskiereGeheimnisse(text) {
    return text.replace(geheimnisMuster(), '•••');
}
/** Die Marke, die `pruefeGraph()` für einen Platzhalter in einer Adresse einsetzt, bevor es sie zerlegt. */
export const URL_PLATZHALTER_MARKE = 'fa-platzhalter-marke';
/** Die Kennung eines Knotens oder einer Kante. */
export const knotenIdSchema = z
    .string()
    .regex(/^[a-z0-9_-]{1,40}$/, 'Eine Kennung besteht aus 1 bis 40 Kleinbuchstaben, Ziffern, _ oder -.');
/**
 * Der Kontext eines Laufs, wie ihn der Läufer in `workflow_lauf.kontext` ablegt
 * (Leseschema nach der Konvention `sql:jsonb`).
 *
 * - `ausloeser`: der Rumpf des Ereignisses, des Webhook-Eingangs oder des Handstarts;
 * - `schritte.<knotenId>`: das Ergebnis eines früheren Knotens (Spec 3.2, Spalte „Ergebnis");
 * - `variablen.<name>`: gesetzt vom Knoten „Variable setzen", immer Text;
 * - `workflow`, `lauf`: Kennung und Name;
 * - `jetzt`: der Zeitpunkt des Schritts, ISO-8601 mit Versatz;
 * - `ablauf.offen` (vom Läufer gepflegt, `WF-laeufer`): die Knoten, die noch auszuführen sind,
 *   in ihrer Reihenfolge. Mehrere Kanten aus einem Knoten laufen nacheinander (Spec 0.3); der
 *   Läufer legt die Nachfolger eines Knotens VOR die übrigen offenen Knoten. Fehlt das Feld, hat
 *   der Lauf noch keinen Schritt (der erste ist der Auslöser). Es ist kein Pfad für Platzhalter
 *   (`KONTEXT_WURZELN` kennt `ablauf` nicht).
 */
export const laufablaufSchema = z.object({ offen: z.array(knotenIdSchema).max(WORKFLOW_HOECHST_SCHRITTE) }).strict();
export const laufkontextSchema = z
    .object({
    ausloeser: z.record(z.string(), z.unknown()),
    schritte: z.record(knotenIdSchema, z.record(z.string(), z.unknown())),
    variablen: z.record(z.string(), z.string()),
    workflow: z.object({ id: z.string().min(1), name: z.string().min(1) }).strict(),
    lauf: z.object({ id: z.string().min(1) }).strict(),
    jetzt: z.string().datetime({ offset: true }),
    ablauf: laufablaufSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Bedingungen (`D-8`)
// ---------------------------------------------------------------------------
export const BEDINGUNG_OPERATOREN = [
    'gleich',
    'ungleich',
    'enthaelt',
    'beginnt_mit',
    'endet_mit',
    'leer',
    'nicht_leer',
    'groesser',
    'kleiner',
    'zwischen',
    'in_liste',
];
export const bedingungOperatorSchema = z.enum(BEDINGUNG_OPERATOREN);
/** Operatoren, die keinen rechten Wert brauchen. */
export const BEDINGUNG_OHNE_RECHTS = ['leer', 'nicht_leer'];
/** Trennzeichen der zwei Grenzen von `zwischen` (`10;20`, beide Grenzen eingeschlossen). */
export const BEDINGUNG_ZWISCHEN_TRENNER = ';';
/**
 * Eine Bedingung: `links` ist ein Pfad in den Kontext (ohne Klammern), `rechts` ein
 * Wert oder Text mit Platzhaltern. `in_liste` trennt mit Komma, `zwischen` mit
 * Semikolon (`10;20`). Bei `leer`/`nicht_leer` bleibt `rechts` leer.
 */
export const bedingungSchema = z
    .object({
    links: kontextPfadSchema,
    operator: bedingungOperatorSchema,
    rechts: z.string().max(1000),
})
    .strict()
    .superRefine((b, ctx) => {
    if (BEDINGUNG_OHNE_RECHTS.includes(b.operator))
        return;
    if (b.rechts.trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rechts'], message: 'Der Vergleichswert fehlt.' });
        return;
    }
    if (b.operator === 'zwischen') {
        const teile = b.rechts.split(BEDINGUNG_ZWISCHEN_TRENNER);
        if (teile.length !== 2 || teile.some((t) => t.trim() === '')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['rechts'],
                message: '„zwischen“ braucht zwei Grenzen, getrennt durch ein Semikolon, etwa 10;20.',
            });
        }
    }
});
export const bedingungsVerknuepfungSchema = z.enum(['alle', 'eine']);
/** Mehrere Bedingungen: `alle` (UND) oder `eine` (ODER). */
export const bedingungsblockSchema = z
    .object({
    verknuepfung: bedingungsVerknuepfungSchema,
    bedingungen: z.array(bedingungSchema).min(1, 'Mindestens eine Bedingung.').max(20),
})
    .strict();
// ---------------------------------------------------------------------------
// Ereigniskatalog (Spec 3.1)
// ---------------------------------------------------------------------------
export const EREIGNISARTEN = [
    'beleg.ausgestellt',
    'beleg.storniert',
    'eingangsbeleg.abgelegt',
    'eingangsrechnung.erfasst',
    'eingangsrechnung.freigegeben',
    'bankumsatz.eingelesen',
    'kontierung.angelegt',
    'kontierung.freigegeben',
    'zahlung.zugeordnet',
    'guthaben.aufladung.bank',
    'shop.aufladung',
    'shop.bestellung',
    'shop.storno',
    'kunde.angelegt',
    'kunde.geaendert',
    'mahnung.erstellt',
    'periode.geschlossen',
];
export const ereignisArtSchema = z.enum(EREIGNISARTEN);
export const ereignisStatusSchema = z.enum(['aktiv', 'geplant']);
export const ereignisFeldTypSchema = z.enum(['text', 'betrag', 'datum', 'id', 'zahl']);
const f = (pfad, typ, beschreibung) => ({
    pfad,
    typ,
    beschreibung,
});
/**
 * Der Ereigniskatalog: welche Fachhandlung einen Workflow auslösen kann und was ihr
 * Rumpf trägt. Beträge als Zeichenketten, Kennungen als UUID, Daten als ISO.
 */
export const EREIGNISKATALOG = [
    {
        art: 'beleg.ausgestellt',
        titel: 'Beleg ausgestellt',
        beschreibung: 'Eine Rechnung, Gutschrift oder ein anderer Ausgangsbeleg wurde ausgestellt.',
        status: 'aktiv',
        felder: [
            f('belegId', 'id', 'Kennung des Belegs'),
            f('nummer', 'text', 'Belegnummer'),
            f('art', 'text', 'Belegart'),
            f('kundeId', 'id', 'Kennung des Kunden'),
            f('kundennummer', 'text', 'Kundennummer'),
            f('brutto', 'betrag', 'Bruttobetrag'),
            f('netto', 'betrag', 'Nettobetrag'),
            f('waehrung', 'text', 'Währung'),
            f('datum', 'datum', 'Belegdatum'),
            f('faellig', 'datum', 'Fälligkeitsdatum'),
        ],
    },
    {
        art: 'beleg.storniert',
        titel: 'Beleg storniert',
        beschreibung: 'Ein ausgestellter Beleg wurde durch einen Stornobeleg aufgehoben.',
        status: 'aktiv',
        felder: [
            f('belegId', 'id', 'Kennung des stornierten Belegs'),
            f('nummer', 'text', 'Nummer des stornierten Belegs'),
            f('stornoBelegId', 'id', 'Kennung des Stornobelegs'),
        ],
    },
    {
        art: 'eingangsbeleg.abgelegt',
        titel: 'Eingangsbeleg abgelegt',
        beschreibung: 'Ein Beleg ist im Eingangskorb angekommen, hochgeladen oder per E-Mail.',
        status: 'aktiv',
        felder: [
            f('incomingDocumentId', 'id', 'Kennung des Eingangsbelegs'),
            f('origin', 'text', 'Herkunft'),
            f('kind', 'text', 'Art des Belegs'),
            f('medientyp', 'text', 'Medientyp der Datei'),
            f('dateiname', 'text', 'Dateiname'),
            f('kreditorId', 'id', 'Kennung des Kreditors, falls bekannt'),
            f('eingangsdatum', 'datum', 'Eingangsdatum'),
        ],
    },
    {
        art: 'eingangsrechnung.erfasst',
        titel: 'Eingangsrechnung erfasst',
        beschreibung: 'Die Angaben einer Eingangsrechnung wurden erfasst.',
        status: 'aktiv',
        felder: [
            f('incomingInvoiceId', 'id', 'Kennung der Eingangsrechnung'),
            f('kreditorId', 'id', 'Kennung des Kreditors'),
            f('nummer', 'text', 'Rechnungsnummer des Kreditors'),
            f('brutto', 'betrag', 'Bruttobetrag'),
            f('datum', 'datum', 'Rechnungsdatum'),
        ],
    },
    {
        art: 'eingangsrechnung.freigegeben',
        titel: 'Eingangsrechnung freigegeben',
        beschreibung: 'Eine erfasste Eingangsrechnung wurde freigegeben.',
        status: 'aktiv',
        felder: [
            f('incomingInvoiceId', 'id', 'Kennung der Eingangsrechnung'),
            f('kreditorId', 'id', 'Kennung des Kreditors'),
            f('nummer', 'text', 'Rechnungsnummer des Kreditors'),
            f('brutto', 'betrag', 'Bruttobetrag'),
            f('datum', 'datum', 'Rechnungsdatum'),
        ],
    },
    {
        art: 'bankumsatz.eingelesen',
        titel: 'Bankumsatz eingelesen',
        beschreibung: 'Eine Bewegung auf einem Bank- oder Zahldienstkonto wurde eingelesen.',
        status: 'aktiv',
        felder: [
            f('bankTransactionId', 'id', 'Kennung der Bewegung'),
            f('konto', 'text', 'Sachkonto des Geldkontos'),
            f('betrag', 'betrag', 'Betrag'),
            f('richtung', 'text', 'Eingang oder Ausgang'),
            f('gegenpartei', 'text', 'Name der Gegenpartei'),
            f('gegenparteiIban', 'text', 'IBAN der Gegenpartei, falls bekannt'),
            f('verwendungszweck', 'text', 'Verwendungszweck'),
            f('datum', 'datum', 'Buchungsdatum'),
        ],
    },
    {
        art: 'kontierung.angelegt',
        titel: 'Kontierung angelegt',
        beschreibung: 'Zu einer Geldbewegung wurde eine Kontierung angelegt.',
        status: 'aktiv',
        felder: [
            f('kontierungId', 'id', 'Kennung der Kontierung'),
            f('bankTransactionId', 'id', 'Kennung der Bewegung'),
            f('herkunft', 'text', 'Wie die Kontierung entstand'),
            f('positionen.0.konto', 'text', 'Sachkonto der ersten Position'),
            f('positionen.0.betrag', 'betrag', 'Betrag der ersten Position'),
        ],
    },
    {
        art: 'kontierung.freigegeben',
        titel: 'Kontierung freigegeben',
        beschreibung: 'Eine Kontierung wurde freigegeben und gebucht.',
        status: 'aktiv',
        felder: [f('kontierungId', 'id', 'Kennung der Kontierung')],
    },
    {
        art: 'zahlung.zugeordnet',
        titel: 'Zahlung zugeordnet',
        beschreibung: 'Eine Zahlung wurde einem Beleg zugeordnet und die Zuordnung freigegeben.',
        status: 'aktiv',
        felder: [
            f('belegId', 'id', 'Kennung des Belegs'),
            f('bankTransactionId', 'id', 'Kennung der Bewegung'),
            f('betrag', 'betrag', 'Zugeordneter Betrag'),
        ],
    },
    {
        art: 'guthaben.aufladung.bank',
        titel: 'Guthaben per Bank aufgeladen',
        beschreibung: 'Ein Kunde hat sein Guthaben per Überweisung aufgeladen.',
        status: 'aktiv',
        felder: [
            f('id', 'id', 'Kennung der Aufladung'),
            f('kunde.nummer', 'text', 'Kundennummer'),
            f('kunde.id', 'id', 'Kennung des Kunden, falls bekannt'),
            f('betrag', 'betrag', 'Aufgeladener Betrag'),
            f('waehrung', 'text', 'Währung'),
            f('wertstellung', 'datum', 'Wertstellung'),
            f('buchungsdatum', 'datum', 'Buchungsdatum'),
            f('verwendungszweck', 'text', 'Verwendungszweck'),
            f('bankkonto.nummer', 'text', 'Sachkonto des Bankkontos'),
            f('bankTransactionId', 'id', 'Kennung der Bewegung'),
        ],
    },
    {
        art: 'shop.aufladung',
        titel: 'Aufladung im Shop',
        beschreibung: 'Der Shop meldet, dass ein Kunde sein Guthaben aufgeladen hat.',
        status: 'geplant',
        felder: [
            f('inboundEventId', 'id', 'Kennung des eingegangenen Ereignisses'),
            f('externalCustomerId', 'text', 'Kundenkennung im Shop'),
            f('amount', 'betrag', 'Aufgeladener Betrag'),
            f('currency', 'text', 'Währung'),
            f('paymentMethod', 'text', 'Zahlweg'),
            f('providerTransactionId', 'text', 'Kennung der Zahlung beim Zahlungsanbieter'),
            f('receivedAt', 'datum', 'Zeitpunkt des Eingangs'),
        ],
    },
    {
        art: 'shop.bestellung',
        titel: 'Bestellung im Shop',
        beschreibung: 'Der Shop meldet eine neue Bestellung.',
        status: 'geplant',
        felder: [
            f('inboundEventId', 'id', 'Kennung des eingegangenen Ereignisses'),
            f('idempotencyKey', 'text', 'Kennung der Meldung im Shop'),
        ],
    },
    {
        art: 'shop.storno',
        titel: 'Stornierte Bestellung im Shop',
        beschreibung: 'Der Shop meldet, dass eine Bestellung storniert wurde.',
        status: 'geplant',
        felder: [
            f('inboundEventId', 'id', 'Kennung des eingegangenen Ereignisses'),
            f('idempotencyKey', 'text', 'Kennung der Meldung im Shop'),
        ],
    },
    {
        art: 'kunde.angelegt',
        titel: 'Kunde angelegt',
        beschreibung: 'Ein neuer Kunde wurde angelegt.',
        status: 'geplant',
        felder: [
            f('kundeId', 'id', 'Kennung des Kunden'),
            f('kundennummer', 'text', 'Kundennummer'),
            f('name', 'text', 'Name'),
        ],
    },
    {
        art: 'kunde.geaendert',
        titel: 'Kunde geändert',
        beschreibung: 'Die Stammdaten eines Kunden wurden geändert.',
        status: 'geplant',
        felder: [
            f('kundeId', 'id', 'Kennung des Kunden'),
            f('kundennummer', 'text', 'Kundennummer'),
            f('name', 'text', 'Name'),
        ],
    },
    {
        art: 'mahnung.erstellt',
        titel: 'Mahnung erstellt',
        beschreibung: 'Zu einem offenen Beleg wurde eine Mahnung erstellt.',
        status: 'geplant',
        felder: [
            f('mahnungId', 'id', 'Kennung der Mahnung'),
            f('belegId', 'id', 'Kennung des Belegs'),
            f('stufe', 'zahl', 'Mahnstufe'),
        ],
    },
    {
        art: 'periode.geschlossen',
        titel: 'Periode geschlossen',
        beschreibung: 'Ein Buchungsmonat wurde geschlossen.',
        status: 'geplant',
        felder: [f('periode', 'text', 'Der geschlossene Monat, etwa 2026-10')],
    },
];
/** Die Beschreibung eines Ereignisses, oder `undefined`. */
export function ereignisBeschreibung(art) {
    return EREIGNISKATALOG.find((e) => e.art === art);
}
// ---------------------------------------------------------------------------
// Einstellungen je Knotentyp (Spec 3.1, 3.2)
// ---------------------------------------------------------------------------
/** Ein Zeitplanausdruck (`D-6`), geprüft mit {@link pruefeZeitplan}. */
export const zeitplanAusdruckSchema = z
    .string()
    .max(200)
    .superRefine((ausdruck, ctx) => {
    const befund = pruefeZeitplan(ausdruck);
    if (befund !== null)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: befund.satz });
});
const platzhalterText = (max) => z.string().max(max);
const pflichtText = (max, meldung) => z.string().trim().min(1, meldung).max(max);
const ausloeserEreignisEinstellungen = z
    .object({
    art: z.string().superRefine((art, ctx) => {
        const e = ereignisBeschreibung(art);
        if (e === undefined) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Das Ereignis „${art}“ gibt es nicht.` });
        }
        else if (e.status !== 'aktiv') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Das Ereignis „${e.titel}“ ist noch nicht verfügbar.`,
            });
        }
    }),
    vorfilter: bedingungsblockSchema.optional(),
})
    .strict();
const ausloeserZeitplanEinstellungen = z.object({ ausdruck: zeitplanAusdruckSchema }).strict();
const ausloeserWebhookEingangEinstellungen = z.object({}).strict();
const ausloeserManuellEinstellungen = z
    .object({ beispielRumpf: z.record(z.string(), z.unknown()).optional() })
    .strict();
const verzoegerungEinheitSchema = z.enum(['minuten', 'stunden', 'tage']);
const MINUTEN_JE_EINHEIT = {
    minuten: 1,
    stunden: 60,
    tage: 24 * 60,
};
const verzoegerungEinstellungen = z
    .object({
    dauer: z.number().int('Die Dauer ist eine ganze Zahl.').min(1, 'Die Dauer ist mindestens 1.'),
    einheit: verzoegerungEinheitSchema,
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.dauer * MINUTEN_JE_EINHEIT[v.einheit] > WORKFLOW_HOECHST_VERZOEGERUNG_MINUTEN) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dauer'],
            message: 'Eine Verzögerung dauert höchstens 30 Tage.',
        });
    }
});
const variableSetzenEinstellungen = z
    .object({
    name: z
        .string()
        .regex(/^[A-Za-z_][A-Za-z0-9_]{0,39}$/, 'Ein Variablenname beginnt mit einem Buchstaben und hat höchstens 40 Zeichen.'),
    wert: platzhalterText(10_000),
})
    .strict();
const protokollEinstellungen = z.object({ text: pflichtText(2000, 'Der Text fehlt.') }).strict();
const stoppEinstellungen = z.object({ grund: z.string().max(500).optional() }).strict();
export const httpMethodeSchema = z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);
export const httpRumpfartSchema = z.enum(['json', 'text', 'formular', 'keiner']);
const httpAnfrageEinstellungen = z
    .object({
    methode: httpMethodeSchema,
    url: z
        .string()
        .trim()
        .max(2000)
        .regex(/^https?:\/\/\S+$/i, 'Die Adresse beginnt mit http:// oder https://.'),
    kopfzeilen: z
        .array(z
        .object({
        name: z.string().regex(/^[A-Za-z0-9-]{1,100}$/, 'Ein Kopfzeilenname besteht aus Buchstaben, Ziffern und -.'),
        wert: platzhalterText(2000),
    })
        .strict())
        .max(20),
    rumpfart: httpRumpfartSchema,
    rumpf: platzhalterText(100_000),
    fehlerIgnorieren: z.boolean(),
})
    .strict();
const webhookSendenEinstellungen = z
    .object({
    endpunktId: pflichtText(200, 'Der Webhook-Endpunkt fehlt.'),
    rumpf: platzhalterText(100_000),
})
    .strict();
const emailAnSchema = z
    .string()
    .trim()
    .max(320)
    .refine((an) => z.string().email().safeParse(an).success || enthaeltPlatzhalter(an), {
    message: 'Der Empfänger ist eine feste E-Mail-Adresse.',
});
const emailSendenEinstellungen = z
    .object({
    an: emailAnSchema,
    betreff: pflichtText(500, 'Der Betreff fehlt.'),
    text: pflichtText(50_000, 'Der Text fehlt.'),
})
    .strict();
/** Ein fester Betrag einer Kontierungsposition oder `gesamt` (der Betrag der Bewegung). */
export const kontierungsBetragSchema = z
    .string()
    .regex(/^(gesamt|\d{1,12}(\.\d{1,2})?)$/, 'Der Betrag ist „gesamt“ oder eine Dezimalzahl wie 12.50.');
const kontierungAnlegenEinstellungen = z
    .object({
    bankTransactionId: pflichtText(200, 'Die Geldbewegung fehlt.'),
    positionen: z
        .array(z
        .object({
        konto: z.string().regex(/^\d{4,6}$/, 'Ein Sachkonto hat 4 bis 6 Ziffern.'),
        steuerschluessel: z.string().trim().min(1).max(10).optional(),
        betrag: kontierungsBetragSchema,
    })
        .strict())
        .min(1, 'Mindestens eine Position.')
        .max(20),
    begruendung: pflichtText(2000, 'Die Begründung fehlt.'),
    kundeId: z.string().trim().min(1).max(200).optional(),
    kreditorId: z.string().trim().min(1).max(200).optional(),
})
    .strict()
    .superRefine((k, ctx) => {
    if (k.positionen.length > 1 && k.positionen.some((p) => p.betrag === 'gesamt')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['positionen'],
            message: '„gesamt“ geht nur bei einer einzigen Position; mehrere Positionen tragen feste Beträge.',
        });
    }
});
/** Das Einstellungsschema je Knotentyp — die eine Quelle für Oberfläche und Server. */
export const EINSTELLUNGEN_SCHEMAS = {
    ausloeser_ereignis: ausloeserEreignisEinstellungen,
    ausloeser_zeitplan: ausloeserZeitplanEinstellungen,
    ausloeser_webhook_eingang: ausloeserWebhookEingangEinstellungen,
    ausloeser_manuell: ausloeserManuellEinstellungen,
    bedingung: bedingungsblockSchema,
    verzoegerung: verzoegerungEinstellungen,
    variable_setzen: variableSetzenEinstellungen,
    protokoll: protokollEinstellungen,
    stopp: stoppEinstellungen,
    http_anfrage: httpAnfrageEinstellungen,
    webhook_senden: webhookSendenEinstellungen,
    email_senden: emailSendenEinstellungen,
    kontierung_anlegen: kontierungAnlegenEinstellungen,
};
const WEITER = ['weiter'];
export const KNOTENKATALOG = [
    {
        typ: 'ausloeser_ereignis',
        art: 'ausloeser',
        titel: 'Bei einem Ereignis',
        beschreibung: 'Startet, sobald im System etwas Bestimmtes geschieht, etwa wenn ein Beleg ausgestellt wird.',
        symbol: 'zap',
        ausgaenge: WEITER,
        aussenwirkung: false,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.ausloeser_ereignis,
    },
    {
        typ: 'ausloeser_zeitplan',
        art: 'ausloeser',
        titel: 'Nach Zeitplan',
        beschreibung: 'Startet regelmässig zu festen Zeiten, etwa jeden Montag um 8 Uhr.',
        symbol: 'clock',
        ausgaenge: WEITER,
        aussenwirkung: false,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.ausloeser_zeitplan,
    },
    {
        typ: 'ausloeser_webhook_eingang',
        art: 'ausloeser',
        titel: 'Bei eingehendem Webhook',
        beschreibung: 'Startet, wenn ein anderes Programm eine signierte Nachricht an die Adresse dieses Workflows schickt.',
        symbol: 'webhook',
        ausgaenge: WEITER,
        aussenwirkung: false,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.ausloeser_webhook_eingang,
    },
    {
        typ: 'ausloeser_manuell',
        art: 'ausloeser',
        titel: 'Von Hand',
        beschreibung: 'Startet, wenn jemand auf „Jetzt ausführen“ klickt.',
        symbol: 'hand',
        ausgaenge: WEITER,
        aussenwirkung: false,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.ausloeser_manuell,
    },
    {
        typ: 'bedingung',
        art: 'bedingung',
        titel: 'Bedingung',
        beschreibung: 'Prüft Werte und führt je nach Ergebnis über „ja“ oder „nein“ weiter.',
        symbol: 'git-branch',
        ausgaenge: ['ja', 'nein'],
        aussenwirkung: false,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.bedingung,
    },
    {
        typ: 'verzoegerung',
        art: 'ablauf',
        titel: 'Warten',
        beschreibung: 'Wartet eine bestimmte Zeit, höchstens 30 Tage, bevor es weitergeht.',
        symbol: 'hourglass',
        ausgaenge: WEITER,
        aussenwirkung: false,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.verzoegerung,
    },
    {
        typ: 'variable_setzen',
        art: 'ablauf',
        titel: 'Variable setzen',
        beschreibung: 'Merkt sich einen Wert unter einem Namen, damit spätere Schritte ihn verwenden können.',
        symbol: 'variable',
        ausgaenge: WEITER,
        aussenwirkung: false,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.variable_setzen,
    },
    {
        typ: 'protokoll',
        art: 'ablauf',
        titel: 'Protokoll',
        beschreibung: 'Schreibt einen Text in das Protokoll des Laufs.',
        symbol: 'scroll-text',
        ausgaenge: WEITER,
        aussenwirkung: false,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.protokoll,
    },
    {
        typ: 'stopp',
        art: 'ablauf',
        titel: 'Stopp',
        beschreibung: 'Beendet den Lauf an dieser Stelle.',
        symbol: 'octagon-x',
        ausgaenge: [],
        aussenwirkung: false,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.stopp,
    },
    {
        typ: 'http_anfrage',
        art: 'aktion',
        titel: 'HTTP-Anfrage',
        beschreibung: 'Ruft eine Adresse im Internet auf und merkt sich die Antwort.',
        symbol: 'globe',
        ausgaenge: WEITER,
        aussenwirkung: true,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.http_anfrage,
    },
    {
        typ: 'webhook_senden',
        art: 'aktion',
        titel: 'Webhook senden',
        beschreibung: 'Schickt eine signierte Nachricht an einen eingerichteten Webhook-Empfänger.',
        symbol: 'send',
        ausgaenge: WEITER,
        aussenwirkung: true,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.webhook_senden,
    },
    {
        typ: 'email_senden',
        art: 'aktion',
        titel: 'E-Mail senden',
        beschreibung: 'Schickt eine E-Mail über den Mailserver des Hauses.',
        symbol: 'mail',
        ausgaenge: WEITER,
        aussenwirkung: true,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.email_senden,
    },
    {
        typ: 'kontierung_anlegen',
        art: 'aktion',
        titel: 'Kontierung anlegen',
        beschreibung: 'Bereitet zu einer Geldbewegung eine Kontierung vor, die ein Mensch freigibt, bevor sie gebucht wird.',
        symbol: 'notebook-pen',
        ausgaenge: WEITER,
        aussenwirkung: true,
        einstellungenSchema: EINSTELLUNGEN_SCHEMAS.kontierung_anlegen,
    },
];
/** Die Beschreibung eines Knotentyps (immer vorhanden, der Katalog ist vollständig — `E-2205`). */
export function knotenBeschreibung(typ) {
    const gefunden = KNOTENKATALOG.find((k) => k.typ === typ);
    if (gefunden === undefined)
        throw new Error(`Knotentyp ohne Katalogeintrag: ${typ}`);
    return gefunden;
}
// ---------------------------------------------------------------------------
// Der Graph (`D-2`, `D-4`)
// ---------------------------------------------------------------------------
const knotenTitelSchema = z.string().trim().min(1, 'Der Titel fehlt.').max(80, 'Der Titel hat höchstens 80 Zeichen.');
const positionSchema = z.object({ x: z.number().finite(), y: z.number().finite() }).strict();
/** Eine Kante: von Knoten zu Knoten, an einer Bedingung über den Ausgang `ja` oder `nein`. */
export const kanteSchema = z
    .object({
    id: knotenIdSchema,
    von: knotenIdSchema,
    nach: knotenIdSchema,
    anschluss: knotenAusgangSchema.optional(),
})
    .strict();
/** Ein Knoten in der Gestaltprüfung: Typ bekannt, Einstellungen irgendein Objekt. */
export const knotenFormSchema = z
    .object({
    id: knotenIdSchema,
    typ: knotentypSchema,
    titel: knotenTitelSchema,
    position: positionSchema,
    einstellungen: z.record(z.string(), z.unknown()),
})
    .strict();
/**
 * Die Gestalt eines Graphen — was ein Entwurf mindestens sein muss, um gespeichert
 * zu werden. Höchstens 1000 Knoten und 2000 Kanten als Schutz vor Unfug; die
 * fachliche Grenze von 50 Knoten meldet {@link pruefeGraph} als Befund.
 */
export const graphFormSchema = z
    .object({
    knoten: z.array(knotenFormSchema).max(1000),
    kanten: z.array(kanteSchema).max(2000),
})
    .strict();
function knotenVon(typ, einstellungen) {
    return z
        .object({
        id: knotenIdSchema,
        typ: z.literal(typ),
        titel: knotenTitelSchema,
        position: positionSchema,
        einstellungen,
    })
        .strict();
}
const S = EINSTELLUNGEN_SCHEMAS;
/** Ein Knoten mit nach `typ` geprüften Einstellungen. */
export const knotenSchema = z.discriminatedUnion('typ', [
    knotenVon('ausloeser_ereignis', S.ausloeser_ereignis),
    knotenVon('ausloeser_zeitplan', S.ausloeser_zeitplan),
    knotenVon('ausloeser_webhook_eingang', S.ausloeser_webhook_eingang),
    knotenVon('ausloeser_manuell', S.ausloeser_manuell),
    knotenVon('bedingung', S.bedingung),
    knotenVon('verzoegerung', S.verzoegerung),
    knotenVon('variable_setzen', S.variable_setzen),
    knotenVon('protokoll', S.protokoll),
    knotenVon('stopp', S.stopp),
    knotenVon('http_anfrage', S.http_anfrage),
    knotenVon('webhook_senden', S.webhook_senden),
    knotenVon('email_senden', S.email_senden),
    knotenVon('kontierung_anlegen', S.kontierung_anlegen),
]);
/** Der Graph mit typisierten Einstellungen (die Struktur prüft erst {@link pruefeGraph}). */
export const graphSchema = z
    .object({
    knoten: z.array(knotenSchema).max(1000),
    kanten: z.array(kanteSchema).max(2000),
})
    .strict();
export const GRAPHBEFUND_CODES = [
    'form',
    'kein_ausloeser',
    'mehrere_ausloeser',
    'zyklus',
    'unerreichbar',
    'kante_unbekannt',
    'anschluss_fehlt',
    'anschluss_ungueltig',
    'zu_viele_knoten',
    'einstellungen_ungueltig',
    'ausloeser_hat_eingang',
    'kontierung_quelle_unzulaessig',
    'url_host_platzhalter',
    'geheimnis_unzulaessig',
    'email_an_platzhalter',
];
export const graphbefundCodeSchema = z.enum(GRAPHBEFUND_CODES);
export const graphbefundSchema = z
    .object({
    code: graphbefundCodeSchema,
    satz: z.string(),
    knotenId: z.string().optional(),
})
    .strict();
const HOECHST_FORMBEFUNDE = 20;
/** Alle Zeichenketten in einem Einstellungsobjekt, mit ihrem Punktpfad (`kopfzeilen.0.wert`). */
function texteIn(wert, pfad) {
    if (typeof wert === 'string')
        return [[pfad.join('.'), wert]];
    if (Array.isArray(wert))
        return wert.flatMap((w, i) => texteIn(w, [...pfad, String(i)]));
    if (typeof wert === 'object' && wert !== null) {
        return Object.entries(wert).flatMap(([s, w]) => texteIn(w, [...pfad, s]));
    }
    return [];
}
function pfadText(pfad) {
    return pfad.length === 0 ? 'Graph' : pfad.map(String).join('.');
}
/**
 * Prüft einen Graphen vollständig (`D-2`, `D-4`) — dieselbe Funktion in Builder und Server.
 *
 * Reihenfolge: zuerst die Gestalt ({@link graphFormSchema}; scheitert sie, kommen nur
 * `form`-Befunde zurück), danach Einstellungen je Knoten, Zahl der Knoten, Auslöser,
 * Kanten und Anschlüsse, Kreise und Erreichbarkeit. Ein Graph ohne Knoten ist nicht
 * leer-gültig, sondern hat keinen Auslöser.
 */
export function pruefeGraph(graph) {
    const befunde = [];
    const form = graphFormSchema.safeParse(graph);
    if (!form.success) {
        for (const issue of form.error.issues.slice(0, HOECHST_FORMBEFUNDE)) {
            befunde.push({
                code: 'form',
                satz: `Der Workflow ist nicht lesbar (${pfadText(issue.path)}): ${issue.message}`,
            });
        }
        return { gueltig: false, befunde };
    }
    const { knoten, kanten } = form.data;
    const name = (k) => `‚${k.titel}‘`;
    const nachId = new Map();
    for (const k of knoten) {
        if (nachId.has(k.id)) {
            befunde.push({ code: 'form', satz: `Die Kennung „${k.id}“ tragen mehrere Knoten.`, knotenId: k.id });
        }
        else {
            nachId.set(k.id, k);
        }
    }
    const kantenIds = new Set();
    for (const kante of kanten) {
        if (kantenIds.has(kante.id))
            befunde.push({ code: 'form', satz: `Die Kennung „${kante.id}“ tragen mehrere Verbindungen.` });
        kantenIds.add(kante.id);
    }
    for (const k of knoten) {
        const ergebnis = EINSTELLUNGEN_SCHEMAS[k.typ].safeParse(k.einstellungen);
        if (!ergebnis.success) {
            const gruende = ergebnis.error.issues
                .slice(0, 3)
                .map((i) => (i.path.length > 0 ? `${pfadText(i.path)}: ${i.message}` : i.message))
                .join('; ');
            befunde.push({
                code: 'einstellungen_ungueltig',
                satz: `Die Einstellungen des Knotens ${name(k)} sind unvollständig oder falsch (${gruende}).`,
                knotenId: k.id,
            });
        }
    }
    // Sicherheitsbefunde je Knoten (Spec-Nachtrag 0.4 Nr. 4, 5, 7, 8).
    const ausloeserTypen = knoten.filter((k) => knotenBeschreibung(k.typ).art === 'ausloeser').map((k) => k.typ);
    for (const k of knoten) {
        for (const [pfad, text] of texteIn(k.einstellungen, [])) {
            const namen = geheimnisNamen(text);
            if (namen.length === 0)
                continue;
            const erlaubterOrt = k.typ === 'http_anfrage' &&
                (pfad === 'rumpf' || /^kopfzeilen\.\d+\.wert$/.test(pfad));
            const ungueltig = namen.filter((n) => !GEHEIMNIS_NAME_MUSTER.test(n));
            if (!erlaubterOrt) {
                befunde.push({
                    code: 'geheimnis_unzulaessig',
                    satz: `Im Knoten ${name(k)} steht ein Geheimnis im Feld „${pfad}“; Geheimnisse sind nur in Kopfzeilen und Rumpf einer HTTP-Anfrage erlaubt.`,
                    knotenId: k.id,
                });
            }
            else if (ungueltig.length > 0) {
                befunde.push({
                    code: 'geheimnis_unzulaessig',
                    satz: `Im Knoten ${name(k)} ist „${ungueltig[0] ?? ''}“ kein gültiger Geheimnisname.`,
                    knotenId: k.id,
                });
            }
        }
        if (k.typ === 'http_anfrage' && typeof k.einstellungen['url'] === 'string') {
            // Nicht per Muster schneiden, sondern so lesen, wie `fetch` liest: jeder Platzhalter wird zu
            // einer festen Marke, `new URL()` zerlegt, und die Marke darf in Schema, Anmeldedaten und
            // Host nicht stehen. Ein Schnitt am ersten „/" uebersieht `https://a@{{x}}/` und ähnliche
            // Formen, die der URL-Leser anders zerlegt als ein Muster.
            const markiert = k.einstellungen['url'].trim().replace(/\{\{[^{}]*\}\}/g, URL_PLATZHALTER_MARKE);
            let zerlegt;
            try {
                zerlegt = /^https?:\/\//i.test(markiert) ? new URL(markiert) : undefined;
            }
            catch {
                zerlegt = undefined;
            }
            if (zerlegt === undefined) {
                befunde.push({
                    code: 'einstellungen_ungueltig',
                    satz: `Im Knoten ${name(k)} ist die Adresse keine gültige http- oder https-Adresse.`,
                    knotenId: k.id,
                });
            }
            else if ([zerlegt.protocol, zerlegt.username, zerlegt.password, zerlegt.hostname].some((teil) => teil.toLowerCase().includes(URL_PLATZHALTER_MARKE))) {
                befunde.push({
                    code: 'url_host_platzhalter',
                    satz: `Im Knoten ${name(k)} steht ein Platzhalter in der Serveradresse; Platzhalter sind erst hinter dem ersten „/“ erlaubt.`,
                    knotenId: k.id,
                });
            }
        }
        if (k.typ === 'email_senden' && typeof k.einstellungen['an'] === 'string' && enthaeltPlatzhalter(k.einstellungen['an'])) {
            befunde.push({
                code: 'email_an_platzhalter',
                satz: `Im Knoten ${name(k)} ist der Empfänger ein Platzhalter; eine E-Mail geht nur an eine feste Adresse.`,
                knotenId: k.id,
            });
        }
        if (k.typ === 'kontierung_anlegen') {
            const quelle = k.einstellungen['bankTransactionId'];
            const pfad = typeof quelle === 'string' ? /^\{\{\s*([^{}\s]+)\s*\}\}$/.exec(quelle.trim())?.[1] : undefined;
            const ausEreignis = ausloeserTypen.length === 1 && ausloeserTypen[0] === 'ausloeser_ereignis';
            if (!ausEreignis || pfad === undefined || !pfad.startsWith('ausloeser.') || !istKontextPfad(pfad)) {
                befunde.push({
                    code: 'kontierung_quelle_unzulaessig',
                    satz: `Der Knoten ${name(k)} darf eine Kontierung nur zu einer Geldbewegung aus dem auslösenden Ereignis anlegen: Auslöser „Bei einem Ereignis“ und als Geldbewegung ein Platzhalter wie {{ausloeser.bankTransactionId}}.`,
                    knotenId: k.id,
                });
            }
        }
    }
    if (knoten.length > WORKFLOW_HOECHST_KNOTEN) {
        befunde.push({
            code: 'zu_viele_knoten',
            satz: `Ein Workflow hat höchstens ${String(WORKFLOW_HOECHST_KNOTEN)} Knoten, dieser hat ${String(knoten.length)}.`,
        });
    }
    const ausloeser = knoten.filter((k) => knotenBeschreibung(k.typ).art === 'ausloeser');
    if (ausloeser.length === 0) {
        befunde.push({ code: 'kein_ausloeser', satz: 'Der Workflow braucht einen Auslöser, der ihn startet.' });
    }
    else if (ausloeser.length > 1) {
        for (const a of ausloeser.slice(1)) {
            befunde.push({
                code: 'mehrere_ausloeser',
                satz: `Ein Workflow hat genau einen Auslöser; ${name(a)} ist ein weiterer.`,
                knotenId: a.id,
            });
        }
    }
    const nachfolger = new Map();
    for (const kante of kanten) {
        const von = nachId.get(kante.von);
        const nach = nachId.get(kante.nach);
        if (von === undefined || nach === undefined) {
            const fehlt = von === undefined ? kante.von : kante.nach;
            befunde.push({
                code: 'kante_unbekannt',
                satz: `Eine Verbindung führt zu einem Knoten „${fehlt}“, den es nicht gibt.`,
                ...(von === undefined ? {} : { knotenId: von.id }),
            });
            continue;
        }
        if (knotenBeschreibung(nach.typ).art === 'ausloeser') {
            befunde.push({
                code: 'ausloeser_hat_eingang',
                satz: `Der Auslöser ${name(nach)} steht am Anfang; keine Verbindung darf in ihn hineinführen.`,
                knotenId: nach.id,
            });
        }
        const ausgaenge = knotenBeschreibung(von.typ).ausgaenge;
        if (ausgaenge.length === 0) {
            befunde.push({
                code: 'anschluss_ungueltig',
                satz: `Nach ${name(von)} geht es nicht weiter; die Verbindung zu ${name(nach)} wird nie benutzt.`,
                knotenId: von.id,
            });
        }
        else if (kante.anschluss === undefined) {
            if (ausgaenge.length > 1) {
                befunde.push({
                    code: 'anschluss_fehlt',
                    satz: `Die Verbindung von ${name(von)} zu ${name(nach)} braucht einen Ausgang: „ja“ oder „nein“.`,
                    knotenId: von.id,
                });
            }
        }
        else if (!ausgaenge.includes(kante.anschluss)) {
            befunde.push({
                code: 'anschluss_ungueltig',
                satz: `${name(von)} hat keinen Ausgang „${kante.anschluss}“.`,
                knotenId: von.id,
            });
        }
        const liste = nachfolger.get(von.id) ?? [];
        liste.push(nach.id);
        nachfolger.set(von.id, liste);
    }
    // Kreise: Tiefensuche mit drei Farben; jede Rückkante ist ein Kreis.
    const farbe = new Map();
    const stapel = [];
    const gemeldet = new Set();
    const besuche = (id) => {
        farbe.set(id, 'grau');
        stapel.push(id);
        for (const nach of nachfolger.get(id) ?? []) {
            const f2 = farbe.get(nach);
            if (f2 === 'grau') {
                const kreis = stapel.slice(stapel.indexOf(nach));
                const schluessel = [...kreis].sort().join('|');
                if (!gemeldet.has(schluessel)) {
                    gemeldet.add(schluessel);
                    const kette = [...kreis, nach].map((kid) => `‚${nachId.get(kid)?.titel ?? kid}‘`).join(' → ');
                    befunde.push({
                        code: 'zyklus',
                        satz: `Die Knoten ${kette} bilden einen Kreis; ein Workflow darf nicht zurückspringen.`,
                        knotenId: nach,
                    });
                }
            }
            else if (f2 === undefined) {
                besuche(nach);
            }
        }
        stapel.pop();
        farbe.set(id, 'schwarz');
    };
    for (const k of nachId.values())
        if (!farbe.has(k.id))
            besuche(k.id);
    // Erreichbarkeit vom einen Auslöser aus.
    const start = ausloeser.length === 1 ? ausloeser[0] : undefined;
    if (start !== undefined) {
        const erreicht = new Set([start.id]);
        const offen = [start.id];
        for (let id = offen.pop(); id !== undefined; id = offen.pop()) {
            for (const nach of nachfolger.get(id) ?? []) {
                if (!erreicht.has(nach)) {
                    erreicht.add(nach);
                    offen.push(nach);
                }
            }
        }
        for (const k of nachId.values()) {
            if (!erreicht.has(k.id) && knotenBeschreibung(k.typ).art !== 'ausloeser') {
                befunde.push({
                    code: 'unerreichbar',
                    satz: `Der Knoten ${name(k)} ist von keinem Auslöser aus erreichbar.`,
                    knotenId: k.id,
                });
            }
        }
    }
    return { gueltig: befunde.length === 0, befunde };
}
/** Ein Graph, den {@link pruefeGraph} ohne Befund lässt — die Form einer scharfen Fassung. */
export const gueltigerGraphSchema = graphSchema.superRefine((graph, ctx) => {
    for (const befund of pruefeGraph(graph).befunde) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: befund.satz, params: { code: befund.code } });
    }
});
/** Der Auslöser eines gültigen Graphen (für den Spiegel `ausloeser_typ` der Fassung). */
export function ausloeserVon(graph) {
    return graph.knoten.find((k) => knotenBeschreibung(k.typ).art === 'ausloeser');
}
// ---------------------------------------------------------------------------
// Die Schnittstelle (Spec 7, Nachtrag 0.4 Nr. 5, 12, 13) — Antworten und Rümpfe
// ---------------------------------------------------------------------------
/** Der Auslöser einer Fassung, wie die Datenbank ihn aus dem Graph spiegelt (`workflow_fassung.ausloeser_typ`). */
export const workflowAusloeserTypSchema = z.enum(['ereignis', 'zeitplan', 'webhook_eingang', 'manuell']);
/** Der gespiegelte Auslösertyp eines Auslöserknotens, oder `undefined` für jeden anderen Knotentyp. */
export function ausloeserTypVon(typ) {
    switch (typ) {
        case 'ausloeser_ereignis':
            return 'ereignis';
        case 'ausloeser_zeitplan':
            return 'zeitplan';
        case 'ausloeser_webhook_eingang':
            return 'webhook_eingang';
        case 'ausloeser_manuell':
            return 'manuell';
        default:
            return undefined;
    }
}
export const workflowFassungStatusSchema = z.enum(['entwurf', 'scharf', 'stillgelegt']);
/** Zustände eines Laufs (Spec 4): `wartet` nach einer Verzögerung oder über dem Stundenlimit. */
export const workflowLaufStatusSchema = z.enum(['laeuft', 'wartet', 'beendet', 'fehler', 'abgebrochen']);
/**
 * Zustände eines Schritts (Spec 4, Nachtrag 0.4 Nr. 3): `laeuft` wird vor einer Aktion mit
 * Aussenwirkung geschrieben, `unklar` heisst „vor einem Abbruch begonnen, Ausgang unbekannt“.
 */
export const workflowSchrittStatusSchema = z.enum([
    'laeuft',
    'ok',
    'fehler',
    'uebersprungen',
    'simuliert',
    'unklar',
]);
const zeitpunkt = z.string().datetime({ offset: true });
/** Der Kopf eines Workflows. */
export const workflowKopfSchema = z
    .object({
    id: z.string().uuid(),
    name: z.string(),
    /** Klein, `[a-z0-9-]`, aus dem Namen beim Anlegen abgeleitet und danach fest (Ereignis `workflow.<nameKey>`). */
    nameKey: z.string(),
    beschreibung: z.string().nullable(),
    aktiv: z.boolean(),
    /** Gesetzt, wenn der Sturmschutz den Workflow angehalten hat (`D-16`). */
    angehaltenGrund: z.string().nullable(),
    angehaltenAm: zeitpunkt.nullable(),
    angelegtAm: zeitpunkt,
    geaendertAm: zeitpunkt.nullable(),
})
    .strict();
/** Eine Fassung mit Graph. Ein Entwurf darf unfertig sein, deshalb die Gestaltform {@link graphFormSchema}. */
export const workflowFassungSchema = z
    .object({
    id: z.string().uuid(),
    workflowId: z.string().uuid(),
    nummer: z.number().int().positive(),
    status: workflowFassungStatusSchema,
    graph: graphFormSchema,
    ausloeserTyp: workflowAusloeserTypSchema.nullable(),
    ausloeserEreignis: z.string().nullable(),
    zeitplan: z.string().nullable(),
    naechsteAusfuehrung: zeitpunkt.nullable(),
    angelegtAm: zeitpunkt,
    /** Zuletzt gespeichert — der Wert für `erwartetGeaendertAm` beim nächsten Speichern. */
    geaendertAm: zeitpunkt,
    scharfgeschaltetVon: z.string().uuid().nullable(),
    scharfgeschaltetAm: zeitpunkt.nullable(),
    stillgelegtAm: zeitpunkt.nullable(),
})
    .strict();
/** Eine Fassung in der Fassungsliste — ohne Graph. */
export const workflowFassungZeileSchema = workflowFassungSchema
    .omit({ graph: true })
    .extend({ knotenAnzahl: z.number().int().nonnegative() })
    .strict();
export const workflowFassungListeSchema = z.object({ fassungen: z.array(workflowFassungZeileSchema) }).strict();
/** Kopf mit scharfer Fassung und Entwurf (`GET /workflows/:id`). */
export const workflowDetailSchema = z
    .object({
    workflow: workflowKopfSchema,
    scharf: workflowFassungSchema.nullable(),
    entwurf: workflowFassungSchema.nullable(),
})
    .strict();
/** Eine Zeile der Workflowliste (Spec 6). */
export const workflowZeileSchema = z
    .object({
    workflow: workflowKopfSchema,
    /** Auslöser der scharfen Fassung, sonst des Entwurfs. */
    ausloeserTyp: workflowAusloeserTypSchema.nullable(),
    ausloeserEreignis: z.string().nullable(),
    scharfNummer: z.number().int().positive().nullable(),
    hatEntwurf: z.boolean(),
    laeufeOk7Tage: z.number().int().nonnegative(),
    laeufeFehler7Tage: z.number().int().nonnegative(),
    letzterLaufAm: zeitpunkt.nullable(),
    letzterLaufStatus: workflowLaufStatusSchema.nullable(),
})
    .strict();
export const workflowListeSchema = z.object({ workflows: z.array(workflowZeileSchema) }).strict();
const workflowNameSchema = z.string().trim().min(1, 'Der Name fehlt.').max(120, 'Der Name hat höchstens 120 Zeichen.');
const workflowBeschreibungSchema = z.string().trim().max(2000, 'Die Beschreibung hat höchstens 2000 Zeichen.');
/** Die Kennungen der Vorlagen (`D-20`). */
export const WORKFLOW_VORLAGEN = [
    'guthaben-aufladung-webhook',
    'eingangsrechnung-email',
    'bankumsatz-kontierung',
    'montag-http-anfrage',
];
export const workflowVorlageSchluesselSchema = z.enum(WORKFLOW_VORLAGEN);
/** `POST /workflows`: ohne `vorlage` ohne Fassung, mit `vorlage` mit einem Entwurf aus der Vorlage. */
export const workflowAnlageSchema = z
    .object({
    name: workflowNameSchema,
    beschreibung: workflowBeschreibungSchema.optional(),
    /** Eine Kennung aus {@link WORKFLOW_VORLAGEN}; geprüft vom Dienst (`WORKFLOW_VORLAGE_UNBEKANNT`). */
    vorlage: z.string().trim().min(1).max(100).optional(),
})
    .strict();
/** `PATCH /workflows/:id`: mindestens ein Feld. `aktiv: true` hebt ein Anhalten des Sturmschutzes auf. */
export const workflowAenderungSchema = z
    .object({
    name: workflowNameSchema.optional(),
    beschreibung: workflowBeschreibungSchema.nullable().optional(),
    aktiv: z.boolean().optional(),
})
    .strict()
    .refine((r) => r.name !== undefined || r.beschreibung !== undefined || r.aktiv !== undefined, {
    message: 'Der Rumpf nennt kein Feld.',
});
/**
 * `PUT /workflows/:id/entwurf`. `erwartetGeaendertAm` ist der `geaendertAm` des Entwurfs, den
 * der Speichernde zuletzt gesehen hat, oder `null`, wenn er keinen kannte (Nachtrag 0.4 Nr. 13).
 */
export const workflowEntwurfRumpfSchema = z
    .object({
    graph: graphFormSchema,
    erwartetGeaendertAm: zeitpunkt.nullable(),
})
    .strict();
/** Antwort auf das Speichern: der Entwurf und die Befunde von {@link pruefeGraph}. */
export const workflowEntwurfAntwortSchema = z
    .object({
    entwurf: workflowFassungSchema,
    befunde: z.array(graphbefundSchema),
})
    .strict();
/** `POST /workflows/:id/starten`. `test: true` simuliert jede Aktion mit Aussenwirkung (`D-19`). */
export const workflowStartRumpfSchema = z
    .object({
    rumpf: z.record(z.string(), z.unknown()).optional(),
    test: z.boolean().optional(),
})
    .strict();
/** Ein Lauf ohne Kontext (Listen). */
export const workflowLaufZeileSchema = z
    .object({
    id: z.string().uuid(),
    workflowId: z.string().uuid(),
    fassungId: z.string().uuid(),
    fassungNummer: z.number().int().positive(),
    ausloeserTyp: workflowAusloeserTypSchema,
    ausloeserBezug: z.string().uuid().nullable(),
    istTest: z.boolean(),
    status: workflowLaufStatusSchema,
    aktuellerKnoten: z.string().nullable(),
    fortsetzenAb: zeitpunkt.nullable(),
    schritteAnzahl: z.number().int().nonnegative(),
    begonnenAm: zeitpunkt,
    beendetAm: zeitpunkt.nullable(),
    fehler: z.string().nullable(),
    kontierungId: z.string().uuid().nullable(),
})
    .strict();
export const workflowLaufListeSchema = z.object({ laeufe: z.array(workflowLaufZeileSchema) }).strict();
/** Ein Schritt im Schrittprotokoll. `eingabe` trägt jedes Geheimnis als `•••`. */
export const workflowSchrittSchema = z
    .object({
    id: z.string().uuid(),
    nr: z.number().int().positive(),
    knotenId: z.string(),
    typ: z.string(),
    status: workflowSchrittStatusSchema,
    eingabe: z.record(z.string(), z.unknown()),
    ergebnis: z.record(z.string(), z.unknown()).nullable(),
    fehler: z.string().nullable(),
    begonnenAm: zeitpunkt,
    beendetAm: zeitpunkt.nullable(),
    dauerMs: z.number().int().nonnegative().nullable(),
})
    .strict();
/** `GET /workflows/laeufe/:laufId`: der Lauf mit Kontext und seinen Schritten. */
export const workflowLaufDetailSchema = z
    .object({
    lauf: workflowLaufZeileSchema.extend({ kontext: laufkontextSchema }).strict(),
    schritte: z.array(workflowSchrittSchema),
})
    .strict();
/** Ein Ereignis aus `workflow_ereignis` (Fehlersuche, Spec 7). */
export const workflowEreignisSchema = z
    .object({
    id: z.string().uuid(),
    art: z.string(),
    bezugId: z.string().uuid().nullable(),
    rumpf: z.record(z.string(), z.unknown()),
    erzeugtAm: zeitpunkt,
    verarbeitetAm: zeitpunkt.nullable(),
    laeufeAngelegt: z.number().int().nonnegative().nullable(),
})
    .strict();
export const workflowEreignisListeSchema = z.object({ ereignisse: z.array(workflowEreignisSchema) }).strict();
/** Ein Geheimnis — ohne Wert; der Wert ist nie lesbar (Nachtrag 0.4 Nr. 5). */
export const workflowGeheimnisSchema = z
    .object({
    id: z.string().uuid(),
    name: z.string(),
    beschreibung: z.string().nullable(),
    aktiv: z.boolean(),
    wertGesetztAm: zeitpunkt,
    angelegtAm: zeitpunkt,
    geaendertAm: zeitpunkt.nullable(),
})
    .strict();
export const workflowGeheimnisListeSchema = z.object({ geheimnisse: z.array(workflowGeheimnisSchema) }).strict();
const geheimnisWertSchema = z.string().min(1, 'Der Wert fehlt.').max(10_000, 'Der Wert hat höchstens 10000 Zeichen.');
export const workflowGeheimnisAnlageSchema = z
    .object({
    name: geheimnisNameSchema,
    wert: geheimnisWertSchema,
    beschreibung: workflowBeschreibungSchema.optional(),
})
    .strict();
/** `PATCH /workflows/geheimnisse/:id`: neuer Wert und/oder Beschreibung, mindestens eines. */
export const workflowGeheimnisAenderungSchema = z
    .object({
    wert: geheimnisWertSchema.optional(),
    beschreibung: workflowBeschreibungSchema.nullable().optional(),
})
    .strict()
    .refine((r) => r.wert !== undefined || r.beschreibung !== undefined, { message: 'Der Rumpf nennt kein Feld.' });
/** Der Webhook-Eingang eines Workflows — die Adresse ohne Secret (`D-18`). */
export const workflowWebhookEingangSchema = z
    .object({
    id: z.string().uuid(),
    workflowId: z.string().uuid(),
    /** Relativ zur Adresse der Installation: `/api/v1/workflows/eingang/<schluessel>`. */
    pfad: z.string(),
    secretGesetztAm: zeitpunkt,
    empfangenZaehler: z.number().int().nonnegative(),
    abgewiesenZaehler: z.number().int().nonnegative(),
    zuletztEmpfangenAm: zeitpunkt.nullable(),
    angelegtAm: zeitpunkt,
})
    .strict();
/** `GET /workflows/:id/webhook-eingang`: `null`, solange keiner eingerichtet ist. */
export const workflowWebhookEingangAntwortSchema = z
    .object({ eingang: workflowWebhookEingangSchema.nullable() })
    .strict();
/** Antwort auf Einrichten/Rotieren: das Secret steht hier genau einmal. */
export const workflowWebhookEingangMitSecretSchema = z
    .object({ eingang: workflowWebhookEingangSchema, secret: z.string() })
    .strict();
/** Ein Knoten im Katalog ohne sein Zod-Schema (das liest die Oberfläche aus dem Vertrag selbst). */
export const workflowKatalogKnotenSchema = z
    .object({
    typ: knotentypSchema,
    art: knotenArtSchema,
    titel: z.string(),
    beschreibung: z.string(),
    symbol: z.string(),
    ausgaenge: z.array(knotenAusgangSchema),
    aussenwirkung: z.boolean(),
})
    .strict();
export const workflowKatalogEreignisSchema = z
    .object({
    art: ereignisArtSchema,
    titel: z.string(),
    beschreibung: z.string(),
    status: ereignisStatusSchema,
    felder: z.array(z.object({ pfad: z.string(), typ: ereignisFeldTypSchema, beschreibung: z.string() }).strict()),
})
    .strict();
export const workflowVorlageSchema = z
    .object({
    schluessel: workflowVorlageSchluesselSchema,
    titel: z.string(),
    beschreibung: z.string(),
    graph: graphFormSchema,
})
    .strict();
/** `GET /workflows/katalog`. */
export const workflowKatalogSchema = z
    .object({
    knoten: z.array(workflowKatalogKnotenSchema),
    ereignisse: z.array(workflowKatalogEreignisSchema),
    vorlagen: z.array(workflowVorlageSchema),
})
    .strict();
/**
 * `POST /workflows/eingang/:schluessel` (`D-18`, Nachtrag 0.4 Nr. 12): die Antwort an den Absender.
 * `wiederholt` ist wahr, wenn dieselbe `X-Fame-Event-Id` schon angekommen war — dann entsteht kein
 * zweiter Lauf, und `laufId` nennt den ersten.
 */
export const workflowEingangAntwortSchema = z
    .object({
    laufId: z.string().uuid().nullable(),
    wiederholt: z.boolean(),
})
    .strict();
//# sourceMappingURL=workflows.js.map